import {useCallback, useMemo, type CSSProperties, type ChangeEvent, type SyntheticEvent} from 'react'
import {
  Box,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
  type SelectChangeEvent,
} from '@mui/material'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify'
import DeleteIcon from '@mui/icons-material/Delete'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../common/store'
import {removeText3D, updateText3D} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import type {SceneText3D, Text3DMaterial} from '../../../common/types/text3dTypes'
import {FONT_3D_OPTIONS, fontHasItalic} from '../../../common/types/text3dTypes'
import type {TextAlign} from '../../../common/types/textTypes'
import PositionPad3D from '../sharedWidgets/PositionPad3D'
import RotationSphere from '../sharedWidgets/RotationSphere'
import SliderRow from '../sharedWidgets/SliderRow'

// Alignment toggle options — exclusive selection, one button per mode
const ALIGN_OPTIONS: {value: TextAlign; labelKey: string; Icon: typeof FormatAlignLeftIcon}[] = [
  {value: 'left', labelKey: 'text3d.alignLeft', Icon: FormatAlignLeftIcon},
  {value: 'center', labelKey: 'text3d.alignCenter', Icon: FormatAlignCenterIcon},
  {value: 'right', labelKey: 'text3d.alignRight', Icon: FormatAlignRightIcon},
  {value: 'justify', labelKey: 'text3d.alignJustify', Icon: FormatAlignJustifyIcon},
]

// ── Text3DItem — single 3D text item with full controls ───────────

interface Text3DItemProps {
  text3d: SceneText3D
}

const Text3DItem = ({text3d}: Text3DItemProps) => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const tokens = useAppTokens()
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  const handleFieldChange = useCallback(
    (field: keyof SceneText3D, value: string | number | boolean) => {
      dispatch(updateText3D({id: text3d.id, changes: {[field]: value}}))
    },
    [dispatch, text3d.id],
  )

  const handleSealUndo = useCallback(() => {
    dispatch(sealUndo())
  }, [dispatch])

  const handleMaterialChange = useCallback(
    (prop: keyof Text3DMaterial, value: number) => {
      dispatch(updateText3D({id: text3d.id, changes: {material: {...text3d.material, [prop]: value}}}))
    },
    [dispatch, text3d.id, text3d.material],
  )

  const handleTextChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleFieldChange('text', e.target.value),
    [handleFieldChange],
  )

  const handleFontChange = useCallback(
    (e: SelectChangeEvent) => {
      handleFieldChange('fontFamily', e.target.value)
      dispatch(sealUndo())
    },
    [handleFieldChange, dispatch],
  )

  const handleSizeChange = useCallback((value: number) => handleFieldChange('fontSize', value), [handleFieldChange])

  const handleDepthChange = useCallback((value: number) => handleFieldChange('depth', value), [handleFieldChange])

  const handleToggleBold = useCallback(() => {
    handleFieldChange('bold', !text3d.bold)
    dispatch(sealUndo())
  }, [handleFieldChange, text3d.bold, dispatch])
  const handleToggleItalic = useCallback(() => {
    handleFieldChange('italic', !text3d.italic)
    dispatch(sealUndo())
  }, [handleFieldChange, text3d.italic, dispatch])

  const handleAlign = useCallback(
    (value: TextAlign) => {
      handleFieldChange('align', value)
      dispatch(sealUndo())
    },
    [handleFieldChange, dispatch],
  )

  const handleColorChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => handleFieldChange('color', e.target.value),
    [handleFieldChange],
  )

  const handleToggleUnlit = useCallback(
    (_: SyntheticEvent, checked: boolean) => {
      handleFieldChange('unlit', checked)
      dispatch(sealUndo())
    },
    [handleFieldChange, dispatch],
  )

  const handleRemove = useCallback(() => dispatch(removeText3D(text3d.id)), [dispatch, text3d.id])

  // CJK fonts have no true italic variant — the toggle is disabled for them
  const italicAvailable = fontHasItalic(text3d.fontFamily)

  // Position handlers
  const handlePosX = useCallback((v: number) => handleFieldChange('x', v), [handleFieldChange])
  const handlePosY = useCallback((v: number) => handleFieldChange('y', v), [handleFieldChange])
  const handlePosZ = useCallback((v: number) => handleFieldChange('z', v), [handleFieldChange])

  // Rotation handlers
  const handleRotX = useCallback((v: number) => handleFieldChange('rotationX', v), [handleFieldChange])
  const handleRotY = useCallback((v: number) => handleFieldChange('rotationY', v), [handleFieldChange])
  const handleRotZ = useCallback((v: number) => handleFieldChange('rotationZ', v), [handleFieldChange])

  // Material slider handlers
  const handleMetalness = useCallback((v: number) => handleMaterialChange('metalness', v), [handleMaterialChange])
  const handleRoughness = useCallback((v: number) => handleMaterialChange('roughness', v), [handleMaterialChange])
  const handleClearcoat = useCallback((v: number) => handleMaterialChange('clearcoat', v), [handleMaterialChange])
  const handleClearcoatRoughness = useCallback(
    (v: number) => handleMaterialChange('clearcoatRoughness', v),
    [handleMaterialChange],
  )
  const handleReflectivity = useCallback((v: number) => handleMaterialChange('reflectivity', v), [handleMaterialChange])

  return (
    <Box sx={sx.root}>
      {/* Header: text input + delete */}
      <Box sx={sx.headerRow}>
        <TextField
          value={text3d.text}
          onChange={handleTextChange}
          onBlur={handleSealUndo}
          size="small"
          variant="outlined"
          multiline
          minRows={1}
          maxRows={6}
          sx={sx.textInput}
          placeholder={t('text3d.placeholder')}
        />
        <IconButton onClick={handleRemove} size="small" sx={sx.deleteBtn} title={t('text3d.remove')}>
          <DeleteIcon sx={{fontSize: 16}} />
        </IconButton>
      </Box>

      {/* Font select */}
      <Select
        value={text3d.fontFamily}
        onChange={handleFontChange}
        size="small"
        fullWidth
        sx={{...sx.select, fontFamily: text3d.fontFamily}}
      >
        {FONT_3D_OPTIONS.map((font) => (
          <MenuItem key={font.label} value={font.label} sx={{fontFamily: font.label, fontSize: 13}}>
            {font.label}
          </MenuItem>
        ))}
      </Select>

      {/* Size slider */}
      <SliderRow
        label={t('text3d.fontSize')}
        value={text3d.fontSize}
        min={1}
        max={50}
        step={0.5}
        unit=""
        onChange={handleSizeChange}
        onChangeCommitted={handleSealUndo}
      />

      {/* Depth slider */}
      <SliderRow
        label={t('text3d.depth')}
        value={text3d.depth}
        min={0.1}
        max={20}
        step={0.1}
        unit=""
        onChange={handleDepthChange}
        onChangeCommitted={handleSealUndo}
      />

      {/* Bold / Italic toggles + Color */}
      <Box sx={sx.controlsRow}>
        <IconButton
          onClick={handleToggleBold}
          size="small"
          sx={{...sx.toggleBtn, ...(text3d.bold ? sx.toggleActive : {})}}
          title={t('text3d.bold')}
        >
          <FormatBoldIcon sx={{fontSize: 16}} />
        </IconButton>
        <IconButton
          onClick={handleToggleItalic}
          size="small"
          disabled={!italicAvailable}
          sx={{...sx.toggleBtn, ...(text3d.italic && italicAvailable ? sx.toggleActive : {})}}
          title={t('text3d.italic')}
        >
          <FormatItalicIcon sx={{fontSize: 16}} />
        </IconButton>
        <Box sx={sx.colorGroup}>
          <Typography sx={sx.label}>{t('text3d.color')}</Typography>
          <input
            type="color"
            value={text3d.color}
            onChange={handleColorChange}
            onBlur={handleSealUndo}
            style={sx.colorInput}
          />
        </Box>
      </Box>

      {/* Alignment toggles */}
      <Box sx={sx.alignRow}>
        {ALIGN_OPTIONS.map(({value, labelKey, Icon}) => (
          <IconButton
            key={value}
            onClick={() => handleAlign(value)}
            size="small"
            sx={{...sx.toggleBtn, ...((text3d.align ?? 'center') === value ? sx.toggleActive : {})}}
            title={t(labelKey)}
          >
            <Icon sx={{fontSize: 16}} />
          </IconButton>
        ))}
      </Box>

      {/* Unlit toggle — no lighting + no shadows when on */}
      <FormControlLabel
        control={<Checkbox checked={text3d.unlit} onChange={handleToggleUnlit} size="small" sx={sx.checkbox} />}
        label={<Typography sx={sx.label}>{t('text3d.unlit')}</Typography>}
        sx={sx.checkboxLabel}
      />

      {/* Material section — only when lit (PBR sliders are meaningless on
          MeshBasicMaterial, mirrors the Sprite3DItem pattern) */}
      {!text3d.unlit && (
        <>
          <Typography sx={sx.sectionLabel}>{t('text3d.material')}</Typography>

          <SliderRow
            label={t('device.materials.metalness')}
            value={text3d.material.metalness}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={handleMetalness}
            onChangeCommitted={handleSealUndo}
          />

          <SliderRow
            label={t('device.materials.roughness')}
            value={text3d.material.roughness}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={handleRoughness}
            onChangeCommitted={handleSealUndo}
          />

          <SliderRow
            label={t('device.materials.clearcoat')}
            value={text3d.material.clearcoat}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={handleClearcoat}
            onChangeCommitted={handleSealUndo}
          />

          <SliderRow
            label={t('device.materials.clearcoatRoughness')}
            value={text3d.material.clearcoatRoughness}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={handleClearcoatRoughness}
            onChangeCommitted={handleSealUndo}
          />

          <SliderRow
            label={t('device.materials.reflectivity')}
            value={text3d.material.reflectivity}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={handleReflectivity}
            onChangeCommitted={handleSealUndo}
          />
        </>
      )}

      {/* Position section — pad + Z slider + inputs */}
      <Typography sx={sx.sectionLabel}>{t('text3d.position')}</Typography>
      <PositionPad3D
        x={text3d.x}
        y={text3d.y}
        z={text3d.z}
        onChangeX={handlePosX}
        onChangeY={handlePosY}
        onChangeZ={handlePosZ}
        onDragEnd={handleSealUndo}
      />

      {/* Rotation section — 3D sphere + inputs */}
      <Typography sx={sx.sectionLabel}>{t('text3d.rotation')}</Typography>
      <RotationSphere
        rotationX={text3d.rotationX}
        rotationY={text3d.rotationY}
        rotationZ={text3d.rotationZ}
        onChangeX={handleRotX}
        onChangeY={handleRotY}
        onChangeZ={handleRotZ}
        onDragEnd={handleSealUndo}
      />
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    padding: '8px',
    marginBottom: '8px',
    borderRadius: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: `1px solid ${tokens.accordion.border}`,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '8px',
  },
  textInput: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      fontSize: 13,
      color: tokens.text.primary,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.accordion.border,
    },
  },
  deleteBtn: {
    color: tokens.text.muted,
    '&:hover': {color: '#ef5350'},
  },
  select: {
    fontSize: 13,
    color: tokens.text.primary,
    marginBottom: '8px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.accordion.border,
    },
  },
  label: {
    fontSize: 11,
    color: tokens.text.secondary,
    whiteSpace: 'nowrap' as const,
    minWidth: 28,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: tokens.text.muted,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    marginTop: '8px',
    marginBottom: '4px',
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  alignRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '4px',
  },
  toggleBtn: {
    color: tokens.text.muted,
    border: `1px solid ${tokens.accordion.border}`,
    borderRadius: '4px',
    width: 28,
    height: 28,
  },
  toggleActive: {
    color: tokens.text.primary,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: tokens.text.secondary,
  },
  colorGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: 'auto',
  },
  colorInput: {
    width: 24,
    height: 24,
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    padding: 0,
  } as CSSProperties,
  checkboxLabel: {
    marginTop: '4px',
    marginBottom: '0px',
    marginLeft: '-4px',
  },
  checkbox: {
    color: tokens.text.muted,
    '&.Mui-checked': {
      color: tokens.text.primary,
    },
    padding: '4px',
  },
})

export default Text3DItem
